import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { DndContext, DragOverlay, pointerWithin, useSensor, useSensors, DragStartEvent, DragEndEvent } from '@dnd-kit/core';
import { InteractiveSafeMouseSensor, InteractiveSafeTouchSensor } from '@/utils/dndSensors';
import { useInfiniteQuery, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { format, isSameDay, startOfMonth, endOfMonth } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faFacebook,
  faInstagram,
  faLinkedin,
  faYoutube,
  faTwitter,
  faTiktok,
  faPinterest,
  faThreads,
  faGoogle,
} from '@fortawesome/free-brands-svg-icons';
import { faCloud } from '@fortawesome/free-solid-svg-icons';
import { useTimezone } from '@/contexts/TimezoneContext';
import { useOnboarding } from '@/contexts/OnboardingContext';
import { CLIENT_CHECKLIST_ITEMS } from '@/services/onboarding.service';
import { useReviewerRestrictions } from '@/hooks/useReviewerRestrictions';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';
import { useAuth } from '@/hooks/useAuth';
import { usePostPermissions } from '@/hooks/usePostPermissions';
import { useNotificationPreferences } from '@/hooks/useNotifications';
import { useNewlyReleased, useReleaseBadges } from '@/hooks/useReleaseSections';
import { teamQueryKeys } from '@/lib/queryKeys';
import { invalidateCalendarOverlay } from '@/lib/queryInvalidation';
import { useInvalidateRelease } from '@/lib/invalidateRelease';
import { devLog } from '@/utils/logger';
import { PostFilterDrawer } from '@/components/creator/PostFilterDrawer';
import { variationsService } from '@/services/variations.service';
import { PostVariation } from '@/types/variations';

// Layout
import { PageHeader } from '@/components/layout/PageHeader';
import { Icon } from '@/components/ui/Icon';

// Calendar Components
import { CalendarHeader } from '@/components/calendar/CalendarHeader';
import { PlanningRunway } from '@/components/calendar/PlanningRunway';
import { CalendarSidebar } from '@/components/calendar/CalendarSidebar';
import { ReleasedShelf, toReleasedShelfVariation, type ReleasedShelfItem } from '@/components/calendar/ReleasedShelf';
import { CalendarGrid } from '@/components/calendar/CalendarGrid';
import { CalendarWeekView } from '@/components/calendar/CalendarWeekView';
import { CalendarDayView } from '@/components/calendar/CalendarDayView';
import { CalendarDayPosts } from '@/components/calendar/CalendarDayPosts';
import { CalendarManagementModal } from '@/components/calendar/CalendarManagementModal';
import { CampaignModal } from '@/components/calendar/CampaignModal';
import { CalendarSelector } from '@/components/calendar/CalendarSelector';
import { PostCreator } from '@/components/PostCreator';
import { TimePickerModal } from '@/components/calendar/TimePickerModal';
import { VariationCreator } from '@/components/VariationCreator';

// Stores
import { useCalendarSettingsStore } from '@/stores/calendarSettingsStore';
import { usePostsStore } from '@/stores/postsStore';

// Services & Types
import { Post, PostChannel, PostVariationSummary, postsApi } from '@/services/posts';
import { calendarService } from '@/services/calendar.service';
import { useCalendars, useDefaultCalendar } from '@/hooks/useCalendar';
import { useCreateMapping } from '@/hooks/useCalendarMappings';
import { useCreateCalendarNote, useUpdateCalendarNote, useDeleteCalendarNote, useCalendarNotesMulti } from '@/hooks/useCalendarNotes';
import { useCalendarHolidaysMulti } from '@/hooks/useCalendarHolidays';
import { CalendarView } from '@/types/calendar';
import { CalendarNote, CreateNoteData, UpdateNoteData } from '@/types/calendar-notes';
import { filterCampaignsForDay } from '@/utils/campaignMatcher';
import { ICONS } from '@/constants/icons';
import { toast } from '@/utils/toast';
import { sharedDayAnchor } from '@/utils/planningRunway';
import { getAlreadyScheduledConflict, buildAlreadyScheduledConflictMessage } from '@/utils/shootRecovery';
import { calendarActionsService } from '@/services/calendar-actions.service';
import { calendarMappingsService } from '@/services/calendar-mappings.service';
import { ResetCalendarDialog } from '@/components/calendar/ResetCalendarDialog';
import { DownloadCalendarDialog } from '@/components/calendar/DownloadCalendarDialog';
import { HolidaysModal } from '@/components/calendar/HolidaysModal';
import { useFeatureLock } from '@/hooks/useFeatureLock';
import { ImportICSDialog } from '@/components/calendar/ImportICSDialog';
import { MappingRulesModal } from '@/components/calendar/MappingRulesModal';
import { ConfirmationModal } from '@/components/ui/ConfirmationModal';
import { getInferredStatus } from '@/utils/calendarStatus';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { openReleaseDrawer } from '@/stores/releaseDrawer.store';
import { releaseItemKey } from '@/types/releaseSections';

// hidden 2026-10-16, redesign pending (flip to true to restore)
const SHOW_PLANNING_RUNWAY = false;

// Platform icons mapping for drag overlay
const platformIcons: Record<string, IconDefinition> = {
  FACEBOOK: faFacebook,
  INSTAGRAM: faInstagram,
  LINKEDIN: faLinkedin,
  TWITTER: faTwitter,
  YOUTUBE: faYoutube,
  GOOGLE_MY_BUSINESS: faGoogle,
  PINTEREST: faPinterest,
  TIKTOK: faTiktok,
  THREADS: faThreads,
  BLUESKY: faCloud,
};

// Platform colors for icons
const platformColors: Record<string, string> = {
  FACEBOOK: 'text-[#1877F2]',
  INSTAGRAM: 'text-[#E4405F]',
  LINKEDIN: 'text-[#0A66C2]',
  TWITTER: 'text-[#1DA1F2]',
  YOUTUBE: 'text-[#FF0000]',
  GOOGLE_MY_BUSINESS: 'text-[#4285F4]',
  PINTEREST: 'text-[#BD081C]',
  TIKTOK: 'text-[#000000]',
  THREADS: 'text-[#000000]',
  BLUESKY: 'text-[#0085FF]',
};

/**
 * Calendar Page
 *
 * Enhanced calendar interface with:
 * - Month/Week/Day views
 * - Sidebar with unscheduled posts (draggable)
 * - Drag-and-drop scheduling
 * - Day detail panel
 * - Multi-calendar support
 * - Platform filtering
 */

// Storage can throw (Safari private mode / blocked storage) - never crash render
const safeGet = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};
const safeSet = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
};
const safeRemove = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
};
/** Personal pinned calendar id for a team (per-user, localStorage only) */
const readPin = (
  userId: number | null | undefined,
  teamId: number | null | undefined
): number | null => {
  if (!userId || !teamId) return null;
  const raw = safeGet(`pinned_calendar_id_${userId}_${teamId}`);
  const parsed = raw ? Number(raw) : NaN;
  return Number.isInteger(parsed) ? parsed : null;
};

export const Calendar = () => {
  const { t } = useTranslation(['calendar', 'posts', 'common']);
  const queryClient = useQueryClient();
  const invalidateRelease = useInvalidateRelease();
  const { formatDateTime, timezone } = useTimezone();
  const { progress, completeChecklistItem, CHECKLIST_ITEMS } = useOnboarding();
  const { canCreateCalendar, isReviewer, getRestrictionMessage } = useReviewerRestrictions();
  const { teamId, hasTeam } = useEffectiveTeamId();
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const permissions = usePostPermissions();

  // Settings store
  const {
    sidebarOpen,
    toggleSidebar,
    setSidebarOpen,
    defaultView,
  } = useCalendarSettingsStore();

  // Posts store (for filter drawer integration)
  const { filters: postsFilters, lastPublishTimestamp } = usePostsStore();

  // On mobile, default to day view and close sidebar
  const isMobileInit = typeof window !== 'undefined' && window.innerWidth < 1024;

  useEffect(() => {
    if (isMobileInit && sidebarOpen) {
      setSidebarOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // State
  const [view, setView] = useState<CalendarView>(isMobileInit ? 'day' : defaultView);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedPosts, setSelectedPosts] = useState<Post[]>([]);
  const [isDayPanelOpen, setIsDayPanelOpen] = useState(false);

  // Staleness guard for refreshDayDrawer to prevent race conditions
  const refreshRequestIdRef = useRef(0);
  const [platformFilter] = useState<string>('ALL'); // Platform filter - to be wired up later
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [selectedCalendarIds, setSelectedCalendarIds] = useState<number[]>([]);
  // Personal (per-user, per-team) pinned default calendar - localStorage only
  const [pinVersion, setPinVersion] = useState(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- pinVersion is a deliberate refresh trigger
  const pinnedCalendarId = useMemo(() => readPin(userId, teamId), [userId, teamId, pinVersion]);
  // Tracks which team the selection was initialized for, so the user may
  // later uncheck everything without the init effect re-selecting
  const selectionInitTeamRef = useRef<number | null | undefined>(undefined);
  const [isPostCreatorOpen, setIsPostCreatorOpen] = useState(false);
  const [editingPostId, setEditingPostId] = useState<number | null>(null);
  const [editingCalendarMappingId, setEditingCalendarMappingId] = useState<number | null>(null);
  const [editingVariation, setEditingVariation] = useState<PostVariation | null>(null);
  const [isVariationCreatorOpen, setIsVariationCreatorOpen] = useState(false);
  const [shootingVariationMappingId, setShootingVariationMappingId] = useState<number | null>(null);

  // DnD state for sidebar drag
  const [activePost, setActivePost] = useState<Post | null>(null);
  const [activeVariation, setActiveVariation] = useState<PostVariation | null>(null);

  // Time picker modal state
  const [mappingModalOpen, setMappingModalOpen] = useState(false);
  const mappingSubmitInFlightRef = useRef(false);
  const [pendingMapping, setPendingMapping] = useState<{
    post: Post;
    date: Date;
    source: 'sidebar' | 'calendar';
    mappingId?: number;
    // For variation drags
    variation?: PostVariation;
    variationId?: number;
    fromShelf?: boolean;
  } | null>(null);

  // Campaign modal state
  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<CalendarNote | null>(null);
  const [campaignDate, setCampaignDate] = useState<Date | null>(null);
  const [campaignShowRepetition, setCampaignShowRepetition] = useState(true);
  const [campaignModalTitle, setCampaignModalTitle] = useState<string | undefined>(undefined);

  // PostCreator modal state (for quick add from calendar)

  // Calendar action dialog states
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  // Holidays and Import ICS states
  const [holidaysModalOpen, setHolidaysModalOpen] = useState(false);
  // Plan gating (festivities): managing holidays opens the upsell instead of the modal when locked
  const festivitiesLock = useFeatureLock('festivities');
  const [importICSDialogOpen, setImportICSDialogOpen] = useState(false);

  // Mapping rules modal state
  const [mappingRulesModalOpen, setMappingRulesModalOpen] = useState(false);

  // Filter drawer state
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

  // Delete confirmation state
  const [deletePostTarget, setDeletePostTarget] = useState<Post | null>(null);
  const [deleteVariationTarget, setDeleteVariationTarget] = useState<{ postId: number; variation: PostVariationSummary } | null>(null);
  const [removeFromCalendarTarget, setRemoveFromCalendarTarget] = useState<Post | null>(null);

  // Fetch calendars
  const { data: calendars, isLoading: isLoadingCalendars } = useCalendars({ isActive: true });
  const mappingCalendars = useMemo(
    () => calendars?.filter((calendar) => selectedCalendarIds.includes(calendar.id)) ?? [],
    [calendars, selectedCalendarIds]
  );
  const { data: defaultCalendar, isLoading: isLoadingDefaultCalendar } = useDefaultCalendar();

  // Calendar mapping mutation
  const createMappingMutation = useCreateMapping();

  // Map of calendarId → { name, color } for tooltip display
  const calendarsById = useMemo(
    () => new Map((calendars ?? []).map(c => [c.id, { name: c.name, color: c.color }])),
    [calendars]
  );

  // Campaign mutations
  const createCampaignMutation = useCreateCalendarNote();
  const updateCampaignMutation = useUpdateCalendarNote();
  const deleteCampaignMutation = useDeleteCalendarNote();

  // Fetch campaigns and holidays for the current month (for the day drawer)
  const currentMonthStart = selectedDate ? startOfMonth(selectedDate) : startOfMonth(new Date());
  const currentMonthEnd = selectedDate ? endOfMonth(selectedDate) : endOfMonth(new Date());
  const { data: monthCampaigns } = useCalendarNotesMulti(
    selectedCalendarIds ?? [],
    format(currentMonthStart, 'yyyy-MM-dd'),
    format(currentMonthEnd, 'yyyy-MM-dd')
  );

  const { data: monthHolidays } = useCalendarHolidaysMulti(
    selectedCalendarIds ?? [],
    format(currentMonthStart, 'yyyy-MM-dd'),
    format(currentMonthEnd, 'yyyy-MM-dd')
  );

  // Filter campaigns and holidays for the selected date
  const selectedDateCampaigns = selectedDate
    ? filterCampaignsForDay(monthCampaigns, selectedDate)
    : [];

  const selectedDateHolidays = selectedDate && monthHolidays
    ? monthHolidays.filter((holiday) => isSameDay(new Date(holiday.holidayDate), selectedDate))
    : [];

  // Initialize calendar selection - wait for calendars to load
  useEffect(() => {
    // Wait for calendars to load before making any selection
    if (!calendars) {
      return;
    }

    // If no calendars available, clear selection and return
    if (calendars.length === 0) {
      selectionInitTeamRef.current = undefined;
      if (selectedCalendarIds.length > 0) {
        setSelectedCalendarIds([]);
      }
      return;
    }

    // Already initialized for this team and user emptied the selection: respect it
    if (selectedCalendarIds.length === 0 && selectionInitTeamRef.current === teamId) {
      return;
    }

    // If we already have a valid selection, keep it
    if (selectedCalendarIds.length > 0) {
      // Validate current selection still exists
      const stillValid = selectedCalendarIds.some(id =>
        calendars.some(cal => cal.id === id)
      );
      if (stillValid) {
        selectionInitTeamRef.current = teamId;
        return;
      }
      // Selection is invalid, clear it and continue to re-select
    }

    selectionInitTeamRef.current = teamId;

    // 0. Personal pinned calendar wins on open (if it still exists)
    const pinnedNow = readPin(userId, teamId);
    if (pinnedNow !== null && calendars.some(cal => cal.id === pinnedNow)) {
      setSelectedCalendarIds([pinnedNow]);
      return;
    }

    // 1. Try to restore from localStorage (team-scoped)
    // Per user + team: a shared browser must never hand one user's selection to the next.
    // The legacy team-only key is ignored (and removed) because its owner is unknown.
    if (teamId) safeRemove(`selected_calendar_ids_${teamId}`);
    const savedIds = userId && teamId ? safeGet(`selected_calendar_ids_${userId}_${teamId}`) : null;
    if (savedIds) {
      try {
        const ids = JSON.parse(savedIds);
        if (Array.isArray(ids) && ids.length > 0) {
          // Validate saved IDs exist in available calendars
          const validIds = ids.filter((id: number) =>
            calendars.some(cal => cal.id === id)
          );
          if (validIds.length > 0) {
            setSelectedCalendarIds(validIds);
            return;
          }
        }
      } catch (error) {
        devLog.error('Failed to parse saved calendar IDs:', error);
      }
    }

    // 2. Fall back to default calendar
    if (defaultCalendar && calendars.some(cal => cal.id === defaultCalendar.id)) {
      setSelectedCalendarIds([defaultCalendar.id]);
      return;
    }

    // 3. Fall back to first available calendar
    setSelectedCalendarIds([calendars[0].id]);
  }, [defaultCalendar, calendars, selectedCalendarIds, teamId, userId]);

  const handlePinChange = useCallback(
    (id: number | null) => {
      if (!userId || !teamId) return;
      const key = `pinned_calendar_id_${userId}_${teamId}`;
      if (id === null) {
        safeRemove(key);
      } else {
        safeSet(key, String(id));
      }
      setPinVersion(v => v + 1);
    },
    [userId, teamId]
  );

  // Persist selected calendars to localStorage (user + team scoped)
  useEffect(() => {
    if (selectedCalendarIds.length > 0 && teamId && userId) {
      safeSet(`selected_calendar_ids_${userId}_${teamId}`, JSON.stringify(selectedCalendarIds));
    }
  }, [selectedCalendarIds, teamId, userId]);

  // Track calendar page visit for onboarding
  useEffect(() => {
    // Only track if:
    // 1. Progress is loaded
    // 2. User hasn't already completed this item
    // 3. Checklist is still showing (not dismissed)
    if (
      progress &&
      !progress.checklistItems?.[CHECKLIST_ITEMS.EXPLORE_CALENDAR] &&
      progress.showChecklist
    ) {
      completeChecklistItem(CHECKLIST_ITEMS.EXPLORE_CALENDAR).catch(() => {
        // Silently fail - non-critical
      });
    }

    // Also auto-complete "browse_calendar" for agency clients
    if (
      progress &&
      !progress.checklistItems?.[CLIENT_CHECKLIST_ITEMS.BROWSE_CALENDAR] &&
      progress.showChecklist &&
      progress.isAgencyClient
    ) {
      completeChecklistItem(CLIENT_CHECKLIST_ITEMS.BROWSE_CALENDAR).catch(() => {
        // Silently fail - non-critical
      });
    }
  }, [progress, completeChecklistItem, CHECKLIST_ITEMS]);

  // Reusable function to refresh day drawer posts
  const refreshDayDrawer = useCallback(async () => {
    if (!selectedDate || !isDayPanelOpen || selectedCalendarIds.length === 0) {
      return;
    }

    const requestId = ++refreshRequestIdRef.current;

    const startOfDay = new Date(selectedDate);
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(selectedDate);
    endOfDay.setHours(23, 59, 59, 999);

    const postsPromises = selectedCalendarIds.map((calendarId) =>
      calendarService.getCalendarPosts(calendarId, {
        startDate: startOfDay.toISOString(),
        endDate: endOfDay.toISOString(),
        platform: platformFilter !== 'ALL' ? platformFilter : undefined,
        timezone,
      })
    );

    const allPostsArrays = await Promise.all(postsPromises);

    // Only update state if this is still the latest request
    if (requestId !== refreshRequestIdRef.current) {
      return;
    }

    const dayPosts = allPostsArrays.flat();
    setSelectedPosts(dayPosts);
    return dayPosts;
  }, [selectedDate, isDayPanelOpen, selectedCalendarIds, platformFilter, timezone]);

  /**
   * Read day posts from React Query cache instead of making a separate API call.
   * Uses the same data source as CalendarGrid.handleDateClick → getPostsForDate.
   * Returns null if cache is empty (caller should fall back to refreshDayDrawer).
   */
  const getDayPostsFromCache = useCallback((date: Date): Post[] | null => {
    const dateKey = format(date, 'yyyy-MM-dd');

    // Get multiCalendar query data from cache (same data CalendarGrid renders)
    const cachedQueries = queryClient.getQueriesData<Record<string, Post[]>>({
      queryKey: [...teamQueryKeys.calendarPosts.all(teamId), 'multi'],
    });

    // No cached queries = cache not populated yet
    if (cachedQueries.length === 0) return null;

    const dayPosts: Post[] = [];
    let hasCacheData = false;

    for (const [, data] of cachedQueries) {
      if (data) {
        hasCacheData = true;
        if (data[dateKey]) {
          dayPosts.push(...data[dateKey]);
        }
      }
    }

    if (!hasCacheData) return null;

    // Sort by scheduledAt (same order as useMultiCalendarPosts)
    dayPosts.sort((a, b) => {
      const aTime = a.scheduledAt ? new Date(a.scheduledAt).getTime() : 0;
      const bTime = b.scheduledAt ? new Date(b.scheduledAt).getTime() : 0;
      return aTime - bTime;
    });

    return dayPosts;
  }, [queryClient, teamId]);

  // Re-fetch day drawer posts when SSE notification updates lastPublishTimestamp
  // This ensures the day drawer shows updated post status/links after publishing
  useEffect(() => {
    if (lastPublishTimestamp === 0) return;
    if (!selectedDate || !isDayPanelOpen) return;

    const refreshFromSSE = async () => {
      try {
        // Wait for calendarPosts cache to be fully updated (SSEContext also
        // invalidates these queries, causing a race with refreshDayDrawer)
        await queryClient.refetchQueries({ queryKey: teamQueryKeys.calendarPosts.all(teamId) });

        // Read from cache (same data source as CalendarGrid)
        const cachedPosts = getDayPostsFromCache(selectedDate);
        if (cachedPosts !== null) {
          setSelectedPosts(cachedPosts);
          devLog.info('Day drawer refreshed via SSE publish notification (from cache)', {
            timestamp: lastPublishTimestamp,
            postsCount: cachedPosts.length,
          });
        } else {
          // Fallback to direct API call if cache is empty
          const posts = await refreshDayDrawer();
          if (posts) {
            devLog.info('Day drawer refreshed via SSE publish notification (from API)', {
              timestamp: lastPublishTimestamp,
              postsCount: posts.length,
            });
          }
        }
      } catch (error) {
        devLog.error('Failed to refresh day drawer after publish:', error);
      }
    };

    refreshFromSSE();
  }, [lastPublishTimestamp, selectedDate, isDayPanelOpen, queryClient, teamId, getDayPostsFromCache, setSelectedPosts, refreshDayDrawer]);

  // Build filter parameters from posts store for sidebar query (memoized to prevent unnecessary re-renders)
  const sidebarFilterParams = useMemo(() => ({
    platforms: postsFilters.platforms,
    dateFrom: postsFilters.dateFrom,
    dateTo: postsFilters.dateTo,
    search: postsFilters.search,
    tags: postsFilters.tags,
    mediaType: postsFilters.mediaType,
    aiGenerated: postsFilters.aiGenerated,
    workflowStatus: postsFilters.workflowStatus,
    publishingStatus: postsFilters.publishingStatus,
    contentStatus: postsFilters.contentStatus,
  }), [
    postsFilters.platforms,
    postsFilters.dateFrom,
    postsFilters.dateTo,
    postsFilters.search,
    postsFilters.tags,
    postsFilters.mediaType,
    postsFilters.aiGenerated,
    postsFilters.workflowStatus,
    postsFilters.publishingStatus,
    postsFilters.contentStatus,
  ]);

  // Constants
  const SIDEBAR_PAGE_SIZE = 20;

  // Sidebar shows ALL posts (any status) - posts can be mapped/scheduled infinitely
  // Only deleted posts are excluded (handled by backend)
  const {
    data: sidebarPostsData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingPosts,
    refetch: refetchSidebarPosts,
  } = useInfiniteQuery({
    queryKey: [...teamQueryKeys.posts.all(teamId), 'sidebar', sidebarFilterParams],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await postsApi.list({
        status: 'ALL', // Fetch ALL posts - posts can be mapped/scheduled infinitely
        platforms: postsFilters.platforms,
        dateFrom: postsFilters.dateFrom,
        dateTo: postsFilters.dateTo,
        search: postsFilters.search,
        tags: postsFilters.tags,
        mediaType: postsFilters.mediaType,
        aiGenerated: postsFilters.aiGenerated,
        workflowStatus: postsFilters.workflowStatus,
        publishingStatus: postsFilters.publishingStatus || undefined,
        contentStatus: postsFilters.contentStatus || undefined,
        sortBy: 'newest', // Show newest posts first
        page: pageParam,
        limit: SIDEBAR_PAGE_SIZE,
      });

      return {
        data: response.data || [],
        pagination: response.pagination,
      };
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { pagination } = lastPage;
      const currentPage = pagination?.page || 1;
      const totalPages = pagination?.pages || 1;

      if (currentPage < totalPages) {
        return currentPage + 1;
      }
      return undefined;
    },
    enabled: hasTeam,
    placeholderData: keepPreviousData,
    staleTime: 30000,
  });

  // Flatten all pages into a single array
  const sidebarPosts = useMemo(() => {
    if (!sidebarPostsData?.pages) return [];
    return sidebarPostsData.pages.flatMap(page => page.data);
  }, [sidebarPostsData]);

  // Newly released nudge (operators of a brand with the release workflow on). The window of
  // "new" is frozen per visit inside useNewlyReleased; shelf posts are a separate query
  // (never postsStore, which the Creator page shares).
  const releaseBadges = useReleaseBadges();
  const isReleaseOperator =
    releaseBadges.data?.workflowActive === true && releaseBadges.data.viewer?.isCreator === true;
  const newlyReleased = useNewlyReleased(isReleaseOperator);
  const { data: notificationPreferences, isError: notificationPreferencesError } = useNotificationPreferences();
  const [shelfDismissed, setShelfDismissed] = useState(false);
  const [mappedShelfKeys, setMappedShelfKeys] = useState<ReadonlySet<string>>(new Set());
  const shelfLeaveTimersRef = useRef<Set<number>>(new Set());
  const [leavingShelfKey, setLeavingShelfKey] = useState<string | null>(null);
  const [landingTarget, setLandingTarget] = useState<{ dayKey: string } | null>(null);
  const [completionVisible, setCompletionVisible] = useState(false);
  const [celebrateShelf, setCelebrateShelf] = useState(false);
  const newlyReleasedPostsQuery = useQuery({
    queryKey: [...teamQueryKeys.posts.all(teamId), 'newly-released', newlyReleased.postIds],
    queryFn: async () => {
      const response = await postsApi.list({
        status: 'ALL',
        platforms: null,
        dateFrom: null,
        dateTo: null,
        search: '',
        postIds: newlyReleased.postIds,
        sortBy: 'newest',
        page: 1,
        limit: 100,
      });
      if (!Array.isArray(response.data)) throw new Error('Newly released posts response is invalid');
      return response.data;
    },
    enabled: hasTeam && !shelfDismissed && newlyReleased.postIds.length > 0,
    staleTime: 30000,
    // Scheduling an item shrinks the id list: keep the list on screen while the smaller one loads
    placeholderData: keepPreviousData,
  });
  const newlyReleasedPosts = useMemo(
    () => newlyReleasedPostsQuery.data ?? [],
    [newlyReleasedPostsQuery.data]
  );
  const shelfItems = useMemo<ReleasedShelfItem[]>(() =>
    newlyReleased.items.flatMap((item) => {
      if (mappedShelfKeys.has(releaseItemKey(item.postId, item.variationId))) return [];
      const post = newlyReleasedPosts.find((candidate) => candidate.id === item.postId);
      if (!post) return [];
      const variation = item.variationId === null
        ? undefined
        : post.variations?.find((candidate) => candidate.id === item.variationId);
      if (item.variationId !== null && !variation) return [];
      return [{ post, variation, releasedAt: item.releasedAt }];
    }),
    [newlyReleased.items, newlyReleasedPosts, mappedShelfKeys]
  );
  const shelfPostsReady = newlyReleasedPostsQuery.isSuccess &&
    !newlyReleasedPostsQuery.isPlaceholderData && !newlyReleasedPostsQuery.isFetching;
  // A release can disappear between the release-list and post-list requests (for
  // example, when its post or variation is deleted). Count it as unavailable only
  // after the exact post-list request completed successfully.
  const unavailableShelfKeys = useMemo<ReadonlySet<string>>(() => {
    if (!shelfPostsReady) return new Set();
    return new Set(newlyReleased.items.flatMap((item) => {
      const post = newlyReleasedPosts.find((candidate) => candidate.id === item.postId);
      const variationExists = item.variationId === null ||
        post?.variations?.some((candidate) => candidate.id === item.variationId);
      return post && variationExists ? [] : [releaseItemKey(item.postId, item.variationId)];
    }));
  }, [shelfPostsReady, newlyReleased.items, newlyReleasedPosts]);
  const accountedShelfKeys = useMemo<ReadonlySet<string>>(
    () => new Set([
      ...shelfItems.map((item) => releaseItemKey(item.post.id, item.variation?.id ?? null)),
      ...newlyReleased.items
        .map((item) => releaseItemKey(item.postId, item.variationId))
        .filter((key) => mappedShelfKeys.has(key)),
      ...unavailableShelfKeys,
    ]),
    [shelfItems, newlyReleased.items, mappedShelfKeys, unavailableShelfKeys]
  );
  const canAcknowledgeShelf = shelfPostsReady && newlyReleased.count > 0 &&
    newlyReleased.count === newlyReleased.items.length &&
    accountedShelfKeys.size === newlyReleased.items.length;
  const excludedShelfKeys = new Set([
    ...newlyReleased.items
      .map((item) => releaseItemKey(item.postId, item.variationId))
      .filter((key) => mappedShelfKeys.has(key)),
    ...unavailableShelfKeys,
  ]);
  const visibleShelfCount = Math.max(0, newlyReleased.count - excludedShelfKeys.size);

  // Arrival feedback happens once per newly seen version, never on every refetch or after scheduling a sibling.
  useEffect(() => {
    if (!teamId || !userId || newlyReleased.items.length === 0 || newlyReleasedPostsQuery.isPending ||
        (notificationPreferences?.celebrationsEnabled === undefined && !notificationPreferencesError)) return;
    const storageKey = `release-shelf-animated:${userId}:${teamId}`;
    const ids = newlyReleased.items.map((item) => releaseItemKey(item.postId, item.variationId));
    let prior: string[] = [];
    try {
      const stored = JSON.parse(localStorage.getItem(storageKey) || '[]');
      if (Array.isArray(stored)) prior = stored.filter((value): value is string => typeof value === 'string');
    } catch { /* storage is optional */ }
    const unplayed = ids.some((id) => !prior.includes(id));
    try { localStorage.setItem(storageKey, JSON.stringify([...new Set([...prior, ...ids])].slice(-150))); } catch { /* storage is optional */ }
    if (!unplayed || notificationPreferences?.celebrationsEnabled !== true || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    setCelebrateShelf(true);
    const timer = window.setTimeout(() => setCelebrateShelf(false), 950);
    return () => window.clearTimeout(timer);
  }, [teamId, userId, newlyReleased.items, newlyReleasedPostsQuery.isPending, notificationPreferences?.celebrationsEnabled, notificationPreferencesError]);

  useEffect(() => {
    setShelfDismissed(false);
    setMappedShelfKeys(new Set());
    setLeavingShelfKey(null);
    setLandingTarget(null);
    setCelebrateShelf(false);
    setCompletionVisible(false);
  }, [teamId]);

  useEffect(() => () => {
    shelfLeaveTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    shelfLeaveTimersRef.current.clear();
  }, [teamId]);

  useEffect(() => {
    if (mappedShelfKeys.size === 0 || visibleShelfCount > 0 || newlyReleased.isLoading) return;
    setCompletionVisible(true);
    const timer = window.setTimeout(() => setCompletionVisible(false), 3200);
    return () => window.clearTimeout(timer);
  }, [mappedShelfKeys, visibleShelfCount, newlyReleased.isLoading]);

  useEffect(() => {
    if (!landingTarget) return;
    if (notificationPreferences?.celebrationsEnabled !== true || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setLandingTarget(null);
      return;
    }
    let cell: HTMLElement | null = null;
    let finishTimer: number | undefined;
    const timer = window.setTimeout(() => {
      cell = document.querySelector<HTMLElement>(`[data-calendar-day="${landingTarget.dayKey}"]`);
      if (!cell) {
        setLandingTarget(null);
        return;
      }
      cell.classList.remove('release-calendar-land');
      void cell.offsetWidth; // Restart the short animation when a second tile lands on the same day.
      cell.classList.add('release-calendar-land');
      finishTimer = window.setTimeout(() => {
        cell?.classList.remove('release-calendar-land');
        setLandingTarget((current) => current?.dayKey === landingTarget.dayKey ? null : current);
      }, 900);
    }, 150);
    return () => {
      window.clearTimeout(timer);
      if (finishTimer !== undefined) window.clearTimeout(finishTimer);
      cell?.classList.remove('release-calendar-land');
    };
  }, [landingTarget, notificationPreferences?.celebrationsEnabled]);

  const handleDismissShelf = useCallback(() => {
    if (canAcknowledgeShelf) newlyReleased.acknowledge(accountedShelfKeys);
    setShelfDismissed(true);
    setCompletionVisible(false);
  }, [canAcknowledgeShelf, newlyReleased, accountedShelfKeys]);

  // If every version has vanished from a successful post-list response, there
  // is no shelf tile or Dismiss control to advance the frozen seen window.
  useEffect(() => {
    if (canAcknowledgeShelf && visibleShelfCount === 0 && unavailableShelfKeys.size > 0) {
      newlyReleased.acknowledge(accountedShelfKeys);
    }
  }, [canAcknowledgeShelf, visibleShelfCount, unavailableShelfKeys, newlyReleased, accountedShelfKeys]);

  // Get total count from first page's pagination
  const totalPostsCount = useMemo(() => {
    if (!sidebarPostsData?.pages?.[0]?.pagination) return 0;
    return sidebarPostsData.pages[0].pagination.total;
  }, [sidebarPostsData]);

  // Sidebar variations - NO LONGER NEEDED
  // Variations are now nested within posts via post.variations

  // DnD sensors
  const sensors = useSensors(
    useSensor(InteractiveSafeMouseSensor, { activationConstraint: { distance: 10 } }),
    useSensor(InteractiveSafeTouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } })
  );

  // Date selection handler
  const handleDateSelect = useCallback((date: Date, posts: Post[]) => {
    setSelectedDate(date);
    setSelectedPosts(posts);
    setIsDayPanelOpen(true);
  }, []);

  // Post selection from sidebar
  const handleSidebarPostSelect = useCallback((post: Post) => {
    // When a post is selected from sidebar, user can then click a date to schedule
    devLog.log('Selected post for scheduling:', post.id);
  }, []);

  const handleScheduleShelfItem = useCallback((item: ReleasedShelfItem) => {
    if (selectedCalendarIds.length === 0) {
      toast.error(t('calendar_error_select_first'));
      return;
    }
    const now = new Date();
    const todayKey = formatInTimeZone(now, timezone, 'yyyy-MM-dd');
    const chosenKey = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : todayKey;
    const dayKey = chosenKey < todayKey ? todayKey : chosenKey;
    const currentHour = Number(formatInTimeZone(now, timezone, 'HH'));
    const hour = dayKey === todayKey ? currentHour + 1 : 9;
    const [year, month, day] = dayKey.split('-').map(Number);
    // TimePickerModal interprets these Date fields as wall time in the account timezone.
    const date = new Date(year, month - 1, day, hour < 24 ? hour : 9, 0, 0, 0);
    if (hour >= 24) date.setDate(date.getDate() + 1);
    setPendingMapping({
      post: item.post,
      date,
      source: 'sidebar',
      variation: item.variation ? toReleasedShelfVariation(item.post, item.variation) : undefined,
      variationId: item.variation?.id,
      fromShelf: true,
    });
    setMappingModalOpen(true);
  }, [selectedCalendarIds, selectedDate, t, timezone]);

  const handleShelfDetails = useCallback((item: ReleasedShelfItem) => {
    openReleaseDrawer({ postId: item.post.id, variationId: item.variation?.id ?? null, source: 'list' });
  }, []);

  // Handle creating a post for a specific date
  const handleCreatePost = useCallback((date?: Date) => {
    // If a date is provided, set time to current time (not midnight)
    if (date) {
      const dateWithCurrentTime = new Date(date);
      const now = new Date();
      dateWithCurrentTime.setHours(now.getHours());
      dateWithCurrentTime.setMinutes(now.getMinutes());
      dateWithCurrentTime.setSeconds(0);
      dateWithCurrentTime.setMilliseconds(0);
      setSelectedDate(dateWithCurrentTime);
    } else {
      setSelectedDate(null);
    }
    setEditingPostId(null);
    setEditingCalendarMappingId(null);
    setIsPostCreatorOpen(true);
    setIsDayPanelOpen(false);
  }, []);

  const handlePlanRunwayDay = useCallback((dayKey: string, brandTimezone: string) => {
    // Anchor the selected day where brand and viewer local dates overlap. If
    // opposite-edge timezones have no overlap, let the planner choose a date
    // in the composer instead of silently prefilling the wrong day.
    const date = sharedDayAnchor(dayKey, brandTimezone, timezone);
    if (!date) toast.info(t('planning_runway_no_shared_day'));
    setSelectedDate(date);
    setEditingPostId(null);
    setEditingCalendarMappingId(null);
    setIsPostCreatorOpen(true);
    setIsDayPanelOpen(false);
  }, [timezone, t]);

  // Handle closing the post creator modal
  const handleClosePostCreator = useCallback(() => {
    setIsPostCreatorOpen(false);
    setEditingPostId(null);
    setEditingCalendarMappingId(null);
    // Note: Query invalidation is handled by onSaveSuccess callback when a post is saved
    // If user just closes without saving, no refresh is needed
  }, []);

  // Handle editing a calendar item - detects if it's a post or variation
  const handleEditPost = useCallback((postId: number, calendarMappingId?: number) => {
    // Find the post to check if it's a variation mapping.
    // Blog calendar entries are READ-ONLY (chips navigate to /blog/:id themselves, never call
    // this handler) and their blogPostId can numerically collide with a real Post id — exclude
    // them so a stray blog entry in selectedPosts never shadows the real post being edited.
    const post = selectedPosts.find(p =>
      p.id === postId && p.source !== 'blog' &&
      (!calendarMappingId || p.mappingId === calendarMappingId)
    );

    // Every post opens in the composer: frozen content (in review, released, scheduled, not yours)
    // shows read-only with the reason; the composer reads the exact item's verdict from the server.
    // Gating here by the calendar entry's status (which is the MAPPING status for mapped posts)
    // made mapped posts unopenable.

    if (post?.variationId && calendarMappingId) {
      // This is a mapped variation - open VariationCreator in shooting mode
      setShootingVariationMappingId(calendarMappingId);
      setIsVariationCreatorOpen(true);
    } else if (calendarMappingId) {
      // This is a mapped post - open PostCreator in shooting mode
      setEditingPostId(postId);
      setEditingCalendarMappingId(calendarMappingId);
      setIsPostCreatorOpen(true);
    } else {
      // Regular post edit
      setEditingPostId(postId);
      setIsPostCreatorOpen(true);
    }
  }, [selectedPosts]);

  // Direct post click from month/week grid - opens editor without going through the day drawer.
  // Unlike handleEditPost, this works directly from the Post object so it does not depend
  // on selectedPosts being populated.
  const handlePostClick = useCallback((post: Post) => {
    // Blog calendar entries are READ-ONLY (chips navigate to /blog/:id themselves, never call
    // this handler). Defensive guard: blogPostId can numerically collide with a real Post id.
    if (post.source === 'blog') return;

    const mappingId = post.mappingId || post.calendarMappings?.[0]?.id;
    const postId = post.id;

    // Every post opens in the composer (read-only when frozen, see handleEditPost)

    if (post.variationId && mappingId) {
      setShootingVariationMappingId(mappingId);
      setIsVariationCreatorOpen(true);
    } else if (mappingId) {
      setEditingPostId(postId);
      setEditingCalendarMappingId(mappingId);
      setIsPostCreatorOpen(true);
    } else {
      setEditingPostId(postId);
      setIsPostCreatorOpen(true);
    }
  }, []);

  // Every calendar Remove entry point passes the exact occurrence here. The
  // action happens only after the shared confirmation below; deleting a Post
  // itself remains a separate action with its own confirmation.
  const requestRemoveFromCalendar = useCallback((post: Post) => {
    // Blog entries are read-only. Their blogPostId may collide with a Post id.
    if (post.source === 'blog' || (!post.mappingId && !post.scheduledAt)) return;
    setRemoveFromCalendarTarget(post);
  }, []);

  const confirmRemoveFromCalendar = useCallback(async () => {
    const post = removeFromCalendarTarget;
    if (!post) return;

    try {
      if (post.mappingId) {
        await calendarMappingsService.deleteMapping(post.mappingId);
      } else if (post.scheduledAt) {
        await postsApi.update(post.id, {
          scheduledAt: null,
          status: 'UNSCHEDULED',
        });
      }

      toast.success(t('posts:post_removed_from_calendar_successfully'));
      invalidateRelease();
      setSelectedPosts(prev => prev.filter(p =>
        post.mappingId
          ? p.mappingId !== post.mappingId
          : !(p.id === post.id && p.source !== 'blog' && !p.mappingId)
      ));
      setRemoveFromCalendarTarget(null);
    } catch (error) {
      devLog.error('Failed to remove post from calendar:', error);
      toast.error(t('error_removing_post_from_calendar'));
      // Keep the confirmation open so the person can retry or cancel.
      throw error;
    }
  }, [removeFromCalendarTarget, invalidateRelease, t]);

  // Handle reschedule (opens post creator with post data)
  const handleReschedulePost = useCallback((postId: number) => {
    setEditingPostId(postId);
    setEditingCalendarMappingId(null);
    setIsPostCreatorOpen(true);
  }, []);

  // Handle editing a post from sidebar
  const handleSidebarEditPost = useCallback((post: Post) => {
    setEditingPostId(post.id);
    setEditingCalendarMappingId(null);
    setIsPostCreatorOpen(true);
  }, []);

  // Handle deleting a post from sidebar
  const handleSidebarDeletePost = useCallback((post: Post) => {
    setDeletePostTarget(post);
  }, []);

  // Confirm delete post
  const confirmDeletePost = useCallback(async () => {
    if (!deletePostTarget) return;

    try {
      await postsApi.delete(deletePostTarget.id);
      toast.success(t('posts:post_deleted_successfully'));

      // Refresh the sidebar
      // Also refreshes the calendar grid (a mapped/scheduled post would otherwise leave a ghost card)
      invalidateRelease();
    } catch (error) {
      devLog.error('Failed to delete post:', error);
      toast.error(t('error_deleting_post'));
    } finally {
      setDeletePostTarget(null);
    }
  }, [deletePostTarget, invalidateRelease, t]);

  // Handle variation selection from sidebar
  const handleSidebarVariationSelect = useCallback((postId: number, variation: PostVariationSummary) => {
    devLog.log('Selected variation for scheduling:', variation.id, 'from post:', postId);
  }, []);

  // Handle editing a variation from sidebar - opens VariationCreator
  const handleSidebarEditVariation = useCallback((postId: number, variation: PostVariationSummary) => {
    // Convert PostVariationSummary to PostVariation for VariationCreator
    const fullVariation: PostVariation = {
      id: variation.id,
      postId,
      title: variation.title,
      caption: variation.caption || '',
      contentStatus: variation.contentStatus || 'DRAFT',
      createdAt: variation.createdAt || new Date().toISOString(),
      updatedAt: variation.updatedAt || new Date().toISOString(),
      post: undefined, // Will be loaded by VariationCreator if needed
      postMedia: variation.postMedia,
      calendarMappings: variation.calendarMappings,
    };
    setEditingVariation(fullVariation);
    setIsVariationCreatorOpen(true);
  }, []);

  // Handle deleting a variation from sidebar
  const handleSidebarDeleteVariation = useCallback((postId: number, variation: PostVariationSummary) => {
    setDeleteVariationTarget({ postId, variation });
  }, []);

  // Confirm delete variation
  const confirmDeleteVariation = useCallback(async () => {
    if (!deleteVariationTarget) return;

    try {
      await variationsService.delete(deleteVariationTarget.postId, deleteVariationTarget.variation.id);
      toast.success(t('variation_deleted_successfully'));

      // Refresh posts list
      // Also refreshes the calendar grid (the deleted variation may have had its own mapping)
      invalidateRelease();
    } catch (error) {
      devLog.error('Failed to delete variation:', error);
      toast.error(t('error_deleting_variation'));
    } finally {
      setDeleteVariationTarget(null);
    }
  }, [deleteVariationTarget, invalidateRelease, t]);

  // Handle closing the variation creator modal
  const handleCloseVariationCreator = useCallback(() => {
    setIsVariationCreatorOpen(false);
    setEditingVariation(null);
    setShootingVariationMappingId(null);
  }, []);

  // Handle variation edit success - refresh sidebar and calendar
  const handleVariationEditSuccess = useCallback(async () => {
    // Refresh calendar posts (in case variation was mapped)
    invalidateRelease();

    // Refresh day drawer data BEFORE closing modal to prevent flicker
    await refreshDayDrawer();

    // Now close modal - drawer already has fresh data
    handleCloseVariationCreator();
  }, [invalidateRelease, handleCloseVariationCreator, refreshDayDrawer]);

  // Calendar action handlers
  const handleDownloadCalendar = useCallback(() => {
    if (!selectedCalendarIds || selectedCalendarIds.length === 0) {
      toast.error(t('calendar_error_select_first'));
      return;
    }
    setDownloadDialogOpen(true);
  }, [selectedCalendarIds, t]);

  const handleResetCalendar = useCallback(() => {
    if (!selectedCalendarIds || selectedCalendarIds.length === 0) {
      toast.error(t('calendar_error_select_first'));
      return;
    }
    setResetDialogOpen(true);
  }, [selectedCalendarIds, t]);

  const handleResetConfirm = useCallback(async (target: 'all' | 'posts' | 'campaigns') => {
    if (!selectedCalendarIds || selectedCalendarIds.length === 0) {
      return;
    }

    setIsResetting(true);
    try {
      const result = await calendarActionsService.resetCalendar(selectedCalendarIds[0], target);

      toast.success(t(`calendar_reset_success_${target}`, { count: result.deleted.posts + result.deleted.campaigns }));

      // Refresh calendar data
      invalidateRelease();
      invalidateCalendarOverlay(queryClient);

      setResetDialogOpen(false);
    } catch (error) {
      toast.error(t('calendar_error_reset_failed'));
    } finally {
      setIsResetting(false);
    }
  }, [selectedCalendarIds, queryClient, invalidateRelease, t]);


  const handleManageHolidays = useCallback(() => {
    if (festivitiesLock.locked) {
      festivitiesLock.showUpsell();
      return;
    }
    setHolidaysModalOpen(true);
  }, [festivitiesLock.locked, festivitiesLock.showUpsell]);

  const handleImportICS = useCallback(() => {
    if (!selectedCalendarIds || selectedCalendarIds.length === 0) {
      toast.error(t('calendar_error_select_first'));
      return;
    }
    setImportICSDialogOpen(true);
  }, [selectedCalendarIds, t]);

  const handleManageMappingRules = useCallback(() => {
    if (!selectedCalendarIds || selectedCalendarIds.length === 0) {
      toast.error(t('calendar_error_select_first'));
      return;
    }
    setMappingRulesModalOpen(true);
  }, [selectedCalendarIds, t]);

  // Handle post rescheduled via drag-and-drop
  const handlePostRescheduled = useCallback(
    async (_postId: number, _newDate: Date) => {
      // Refresh the day panel if it's showing the original date
      refreshDayDrawer();
    },
    [refreshDayDrawer]
  );

  // Close the day panel
  const handleCloseDayPanel = useCallback(() => {
    setIsDayPanelOpen(false);
    setSelectedDate(null);
  }, []);

  // DnD handlers
  const handleDragStart = (event: DragStartEvent) => {
    // Auto-close sidebar on mobile so calendar days are visible for drop targets
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }

    // Handle both sidebar posts and calendar posts
    const dragId = String(event.active.id);

    if (event.active.data.current?.type === 'release-shelf-post') {
      setActivePost(event.active.data.current.post as Post);
      setActiveVariation(null);
      return;
    }
    if (event.active.data.current?.type === 'release-shelf-variation') {
      setActiveVariation(event.active.data.current.variation as PostVariation);
      setActivePost(null);
      return;
    }

    if (dragId.startsWith('sidebar-post-')) {
      const postId = dragId.replace('sidebar-post-', '');
      const post =
        sidebarPosts.find(p => String(p.id) === postId) ??
        newlyReleasedPosts.find(p => String(p.id) === postId);
      setActivePost(post || null);
      setActiveVariation(null);
    } else if (dragId.startsWith('sidebar-variation-')) {
      // Variation being dragged from sidebar
      const variation = event.active.data.current?.variation as PostVariation | undefined;
      setActiveVariation(variation || null);
      setActivePost(null);
    } else if (dragId.startsWith('calendar-post-')) {
      // Calendar post being dragged
      const post = event.active.data.current?.post as Post | undefined;
      setActivePost(post || null);
      setActiveVariation(null);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const draggedPost = activePost;
    const draggedVariation = activeVariation;
    setActivePost(null);
    setActiveVariation(null);

    const { active, over } = event;
    if (!over || !active) return;

    const dropType = over.data.current?.type;

    // Check if dropped on a valid target (calendar-day or calendar-time-block)
    if (dropType !== 'calendar-day' && dropType !== 'calendar-time-block') {
      return;
    }

    // Get target date (works for both day and time block drops)
    const targetDateString = over.data.current!.date as string;
    const targetDate = new Date(targetDateString);

    // Block drops on past dates
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const targetDateOnly = new Date(targetDate);
    targetDateOnly.setHours(0, 0, 0, 0);

    if (targetDateOnly < today) {
      toast.error(t('cannot_drop_on_past_date', { defaultValue: 'Cannot schedule posts on past dates' }));
      return;
    }

    // Handle calendar post being moved
    if (active.data.current?.type === 'calendar-post') {
      const { post, sourceDate, mappingId } = active.data.current as { post: Post; sourceDate: string; mappingId?: number };

      if (!post) return;

      // Check if post is being dragged to a different calendar
      const sourceCalendarId = post.calendarMappings?.find((entry) => entry.id === mappingId)?.calendarId
        ?? post.calendarMappings?.[0]?.calendarId;
      const targetCalendarId = selectedCalendarIds[0]; // Currently selected calendar

      // If dragging to a different calendar, show error and prevent
      if (sourceCalendarId && targetCalendarId && sourceCalendarId !== targetCalendarId) {
        toast.error(
          t('cannot_move_to_different_calendar', {
            defaultValue: 'Cannot move post to a different calendar. Use the sidebar to schedule to multiple calendars.',
          })
        );
        return; // Prevent the drag
      }

      const source = new Date(sourceDate);

      // For time block drops, calculate specific time
      if (dropType === 'calendar-time-block') {
        const block = over.data.current!.block as string;

        // Extract time from block ID
        let targetHour = 12; // Default to noon
        if (block === 'night') {
          targetHour = 1; // Unambiguous overnight default even on a DST fallback day
        } else if (block === 'morning') {
          targetHour = 9; // 9 AM
        } else if (block === 'afternoon') {
          targetHour = 14; // 2 PM
        } else if (block === 'evening') {
          targetHour = 19; // 7 PM
        } else if (block.startsWith('hour-')) {
          // Hourly view: extract hour from block ID
          targetHour = parseInt(block.replace('hour-', ''), 10);
        }

        // Create date with specific time
        const targetWithTime = new Date(targetDate);
        targetWithTime.setHours(targetHour, 0, 0, 0);

        // Open time picker modal with pre-filled time
        // Include mappingId to ensure we move the correct mapping when post has multiple
        setPendingMapping({ post, date: targetWithTime, source: 'calendar', mappingId });
        setMappingModalOpen(true);
        return;
      }

      // For day drops, check if same day
      if (source.toDateString() === targetDate.toDateString()) {
        return;
      }

      // Preserve the post's original time when moving to another day
      const targetWithOriginalTime = new Date(targetDate);
      if (post.scheduledAt) {
        const originalTime = new Date(post.scheduledAt);
        targetWithOriginalTime.setHours(
          originalTime.getHours(),
          originalTime.getMinutes(),
          0,
          0
        );
      } else {
        // Default to current hour + 1 if post has no scheduled time
        const now = new Date();
        const defaultHour = Math.min(now.getHours() + 1, 23);
        targetWithOriginalTime.setHours(defaultHour, 0, 0, 0);
      }

      // Open time picker modal for moving the post
      // Include mappingId to ensure we move the correct mapping when post has multiple
      setPendingMapping({ post, date: targetWithOriginalTime, source: 'calendar', mappingId });
      setMappingModalOpen(true);
      return;
    }

    // Handle sidebar post being dragged (original logic)
    if (draggedPost) {
      // ============================================================================
      // MAPPING VS SCHEDULING BUSINESS RULES
      // ============================================================================
      // MAPPING (placing on calendar) - No restrictions:
      //   - Any post can be mapped regardless of status (scheduled, published, failed)
      //   - Same post can be mapped to multiple dates
      //   - All user types can map (normal teams, agency staff, clients)
      //
      // SCHEDULING (shooting/publishing) - Restricted by canSchedulePost middleware:
      //   - Normal teams: Can schedule any post
      //   - Agency + CB ON: Can schedule any post
      //   - Agency + CB OFF (staff): Only client-approved posts
      //   - Agency + CB OFF (client): Cannot schedule at all
      // ============================================================================

      // Check if a calendar is selected
      if (!selectedCalendarIds || selectedCalendarIds.length === 0) {
        toast.error(t('calendar_error_select_first'));
        return;
      }

      // For time block drops, calculate specific time
      if (dropType === 'calendar-time-block') {
        const block = over.data.current!.block as string;

        let targetHour = 12;
        if (block === 'night') {
          targetHour = 1;
        } else if (block === 'morning') {
          targetHour = 9;
        } else if (block === 'afternoon') {
          targetHour = 14;
        } else if (block === 'evening') {
          targetHour = 19;
        } else if (block.startsWith('hour-')) {
          targetHour = parseInt(block.replace('hour-', ''), 10);
        }

        const targetWithTime = new Date(targetDate);
        targetWithTime.setHours(targetHour, 0, 0, 0);

        setPendingMapping({ post: draggedPost, date: targetWithTime, source: 'sidebar', fromShelf: active.data.current?.type === 'release-shelf-post' });
        setMappingModalOpen(true);
        return;
      }

      // Open time picker modal instead of scheduling directly
      // Set default time to current time (rounded to next hour) instead of midnight
      const now = new Date();
      const targetWithTime = new Date(targetDate);
      // Use current hour + 1 (rounded up) as default, capped at 23
      const defaultHour = Math.min(now.getHours() + 1, 23);
      targetWithTime.setHours(defaultHour, 0, 0, 0);

      setPendingMapping({ post: draggedPost, date: targetWithTime, source: 'sidebar', fromShelf: active.data.current?.type === 'release-shelf-post' });
      setMappingModalOpen(true);
      return;
    }

    // Handle sidebar variation being dragged
    if (draggedVariation) {
    // Check if a calendar is selected
    if (!selectedCalendarIds || selectedCalendarIds.length === 0) {
      toast.error(t('calendar_error_select_first'));
      return;
    }

    // For time block drops, calculate specific time
    if (dropType === 'calendar-time-block') {
      const block = over!.data.current!.block as string;

      let targetHour = 12;
      if (block === 'night') {
        targetHour = 1;
      } else if (block === 'morning') {
        targetHour = 9;
      } else if (block === 'afternoon') {
        targetHour = 14;
      } else if (block === 'evening') {
        targetHour = 19;
      } else if (block.startsWith('hour-')) {
        targetHour = parseInt(block.replace('hour-', ''), 10);
      }

      const targetWithTime = new Date(targetDate);
      targetWithTime.setHours(targetHour, 0, 0, 0);

      // Create a "fake" post from variation for the time picker modal
      // Use effective values (variation's own or parent's)
      const effectivePost: Post = {
        id: draggedVariation!.postId,
        caption: draggedVariation!.caption ?? draggedVariation!.post?.caption ?? '',
        postMedia: draggedVariation!.postMedia ?? draggedVariation!.post?.postMedia ?? [],
        postChannels: (draggedVariation!.post?.postChannels ?? []) as PostChannel[],
        labels: draggedVariation!.labels ?? draggedVariation!.post?.labels ?? [],
        // Other required fields
        status: 'MAPPED' as const,
        scheduledAt: null,
        publishedAt: null,
        aiGenerated: false,
        createdAt: draggedVariation!.createdAt,
        updatedAt: draggedVariation!.updatedAt,
      };

      setPendingMapping({
        post: effectivePost,
        date: targetWithTime,
        source: 'sidebar',
        variation: draggedVariation!,
        variationId: draggedVariation!.id,
        fromShelf: active.data.current?.type === 'release-shelf-variation',
      });
      setMappingModalOpen(true);
      return;
    }

    // Open time picker modal for day drops
    const now = new Date();
    const targetWithTime = new Date(targetDate);
    const defaultHour = Math.min(now.getHours() + 1, 23);
    targetWithTime.setHours(defaultHour, 0, 0, 0);

    const effectivePost: Post = {
      id: draggedVariation!.postId,
      caption: draggedVariation!.caption ?? draggedVariation!.post?.caption ?? '',
      postMedia: draggedVariation!.postMedia ?? draggedVariation!.post?.postMedia ?? [],
      postChannels: (draggedVariation!.post?.postChannels ?? []) as PostChannel[],
      labels: draggedVariation!.labels ?? draggedVariation!.post?.labels ?? [],
      status: 'MAPPED' as const,
      scheduledAt: null,
      publishedAt: null,
      aiGenerated: false,
      createdAt: draggedVariation!.createdAt,
      updatedAt: draggedVariation!.updatedAt,
    };

    setPendingMapping({
      post: effectivePost,
      date: targetWithTime,
      source: 'sidebar',
      variation: draggedVariation!,
      variationId: draggedVariation!.id,
      fromShelf: active.data.current?.type === 'release-shelf-variation',
    });
    setMappingModalOpen(true);
    return;
  }

  // If neither post nor variation is being dragged, exit
  return;
};

  // Handle time picker modal confirm
  const handleMappingConfirm = async (plannedDate: Date, calendarId?: number) => {
    if (!pendingMapping || mappingSubmitInFlightRef.current) {
      return;
    }
    // The guard is set before the first await, so a second click or Enter cannot
    // create another mapping while React is still rendering the pending state.
    mappingSubmitInFlightRef.current = true;

    try {
      const { post, source, mappingId } = pendingMapping;

      // SIDEBAR DRAG: Always create a new mapping (same post can be mapped multiple times)
      if (source === 'sidebar') {
        if (!selectedCalendarIds || selectedCalendarIds.length === 0) {
          toast.error(t('calendar_error_select_first'));
          return;
        }

        const targetCalendarId = calendarId || selectedCalendarIds[0];
        await createMappingMutation.mutateAsync({
          postId: post.id,
          variationId: pendingMapping.variationId,
          calendarId: targetCalendarId,
          plannedDate: plannedDate.toISOString(),
        });

        if (pendingMapping.fromShelf) {
          const key = releaseItemKey(post.id, pendingMapping.variationId ?? null);
          const dayKey = formatInTimeZone(plannedDate, timezone, 'yyyy-MM-dd');
          const showPlacementMotion = notificationPreferences?.celebrationsEnabled === true &&
            !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          if (window.innerWidth < 1024) {
            setView('day');
            const [year, month, day] = dayKey.split('-').map(Number);
            setSelectedDate(new Date(year, month - 1, day));
          }
          if (showPlacementMotion) {
            setLeavingShelfKey(key);
            setLandingTarget({ dayKey });
            const timer = window.setTimeout(() => {
              setMappedShelfKeys((current) => new Set([...current, key]));
              setLeavingShelfKey(null);
              shelfLeaveTimersRef.current.delete(timer);
            }, 300);
            shelfLeaveTimersRef.current.add(timer);
          } else {
            setMappedShelfKeys((current) => new Set([...current, key]));
          }
        }

        // Update toast message for variations
        if (pendingMapping.variationId) {
          toast.success(t('calendar_variation_mapped', { datetime: formatDateTime(plannedDate), defaultValue: `Variation mapped to ${formatDateTime(plannedDate)}` }));
        } else {
          toast.success(t('calendar_post_mapped', { datetime: formatDateTime(plannedDate) }));
        }
      }
      // CALENDAR DRAG: Move existing mapping or update schedule
      // Use mappingId from drag data (not post.mappingId) to ensure correct mapping is moved
      // when the same post has multiple mappings on different dates
      else if (mappingId) {
        // Update existing mapping - post stays on calendar, just different day
        await calendarMappingsService.moveDate(mappingId, {
          newDate: plannedDate,
        });
        // Optimistic cache patch so the Post/Variation Creator seeds the NEW plannedDate on reopen (#360)
        queryClient.setQueryData<{ plannedDate: string }>(
          ['calendar-mapping', mappingId],
          (old) => (old ? { ...old, plannedDate: plannedDate.toISOString() } : old),
        );
        toast.success(t('calendar_post_moved', { datetime: formatDateTime(plannedDate) }));
      } else if (post.scheduledAt) {
        // Update scheduled post - post stays on calendar, just different time
        await postsApi.update(post.id, {
          scheduledAt: plannedDate.toISOString(),
        });
        toast.success(t('calendar_post_rescheduled', { datetime: formatDateTime(plannedDate) }));
      }

      // Refresh calendar data:
      // 1. invalidateQueries marks ALL calendarPosts queries as stale (including
      //    unmounted views like week/month) so they refetch when the user switches view.
      //    Without this, the 5-min staleTime causes other views to serve stale cache.
      // 2. refetchQueries (awaited) forces the ACTIVE view to refetch immediately,
      //    ensuring the day drawer gets fresh data before it reads from cache.
      // Also covers the singular calendar-mapping key that seeds the Creator scheduled time (#360)
      invalidateRelease();
      await queryClient.refetchQueries({ queryKey: teamQueryKeys.calendarPosts.all(teamId) });

      // Read day posts from React Query cache (just refreshed by refetchQueries above)
      // This uses the same data source as CalendarGrid.handleDateClick, avoiding
      // the separate API call in refreshDayDrawer that can return inconsistent data
      if (isDayPanelOpen && selectedDate) {
        const cachedPosts = getDayPostsFromCache(selectedDate);
        if (cachedPosts !== null) {
          setSelectedPosts(cachedPosts);
        } else {
          await refreshDayDrawer();
        }
      }

      // Now close modal - drawer already has fresh data
      setMappingModalOpen(false);
      setPendingMapping(null);

      devLog.log(`Post ${post.id} updated to ${formatDateTime(plannedDate)}`);
    } catch (err) {
      devLog.error('Failed to update post:', err);
      // Same-day double-publish rejected (409 already_scheduled): the server did NOT
      // move the mapping. Show the localised warning, close the modal and refetch so
      // the card is rendered at its server-side (original) day.
      const conflict = getAlreadyScheduledConflict(err);
      if (conflict) {
        toast.warning(
          buildAlreadyScheduledConflictMessage(
            conflict,
            (_key, options) => t('posts:already_scheduled_conflict', options),
            formatDateTime
          )
        );
        if (pendingMapping?.mappingId) {
          // Undo any stale seeded plannedDate for the Creator
          await queryClient.invalidateQueries({ queryKey: ['calendar-mapping', pendingMapping.mappingId] });
        }
        invalidateRelease();
        await queryClient.refetchQueries({ queryKey: teamQueryKeys.calendarPosts.all(teamId) });
        if (isDayPanelOpen && selectedDate) {
          const cachedPosts = getDayPostsFromCache(selectedDate);
          if (cachedPosts !== null) {
            setSelectedPosts(cachedPosts);
          } else {
            await refreshDayDrawer();
          }
        }
        setMappingModalOpen(false);
        setPendingMapping(null);
        return;
      }
      toast.error(t('calendar_error_update_failed'));
    } finally {
      mappingSubmitInFlightRef.current = false;
    }
  };

  // Handle time picker modal close
  const handleMappingModalClose = () => {
    if (!createMappingMutation.isPending && !mappingSubmitInFlightRef.current) {
      setMappingModalOpen(false);
      setPendingMapping(null);
    }
  };

  // Campaign handlers - ready to be wired to UI (day panel button, campaign click)
  const openCampaignModal = useCallback((
    date?: Date,
    campaign?: CalendarNote,
    showRepetition = true,
    title?: string
  ) => {
    setCampaignDate(date || null);
    setEditingCampaign(campaign || null);
    setCampaignShowRepetition(showRepetition);

    // Non-repeating pillars and campaigns have no persisted discriminator.
    let modalTitle = title;
    if (!modalTitle && campaign) {
      modalTitle = campaign.repeatType
        ? t('edit_content_pillar', { defaultValue: 'Edit Content Pillar' })
        : t('edit_calendar_item', { defaultValue: 'Edit calendar item' });
    }
    setCampaignModalTitle(modalTitle);
    setCampaignModalOpen(true);
  }, [t]);

  // Handle campaign submit
  const handleCampaignSubmit = useCallback((data: CreateNoteData | { id: number; data: UpdateNoteData }) => {
    if ('id' in data) {
      // Update existing campaign
      updateCampaignMutation.mutate({
        calendarId: selectedCalendarIds[0] || 0,
        noteId: data.id,
        data: data.data,
      }, {
        onSuccess: () => {
          setCampaignModalOpen(false);
          toast.success(editingCampaign?.repeatType
            ? t('calendar_content_pillar_updated')
            : t('calendar_item_updated'));
        },
        onError: () => {
          toast.error(t('common:error_generic'));
        },
      });
    } else {
      createCampaignMutation.mutate(data, {
        onSuccess: () => {
          setCampaignModalOpen(false);
          toast.success(t('calendar_campaign_created'));
        },
        onError: () => {
          toast.error(t('common:error_generic'));
        },
      });
    }
  }, [createCampaignMutation, updateCampaignMutation, selectedCalendarIds, editingCampaign, t]);

  // Handle campaign delete
  const handleDeleteCampaign = useCallback((campaignId: number) => {
    if (!selectedCalendarIds || selectedCalendarIds.length === 0) return;

    deleteCampaignMutation.mutate({
      calendarId: selectedCalendarIds[0],
      noteId: campaignId,
    }, {
      onSuccess: () => {
        toast.success(t('calendar_campaign_deleted'));
      },
      onError: () => {
        toast.error(t('calendar_campaign_delete_error'));
      }
    });
  }, [deleteCampaignMutation, selectedCalendarIds, t]);

  return (
    <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="flex flex-col min-h-[calc(100vh-4rem)]">
          {/* Page Header with branding */}
          <PageHeader
            title={
              <>
                {/* eslint-disable-next-line i18next/no-literal-string */}
                <span className="text-primary-500">Ä</span>gile Mapping
              </>
            }
            subtitle={t('calendar_subtitle')}
            icon={ICONS.CALENDAR}
            headerType="calendar"
            documentTitle={t('agile_mapping')}
          />

          {/* Newly released versions, ready for a deliberate placement on the calendar. */}
          {!shelfDismissed && (visibleShelfCount > 0 || completionVisible) && (
            <ReleasedShelf
              items={shelfItems}
              count={visibleShelfCount}
              isLoading={newlyReleasedPostsQuery.isPending}
              celebrate={celebrateShelf}
              allowMotion={notificationPreferences?.celebrationsEnabled === true}
              leavingKey={leavingShelfKey}
              completion={completionVisible && visibleShelfCount === 0}
              onSchedule={handleScheduleShelfItem}
              onDetails={handleShelfDetails}
              onDismiss={handleDismissShelf}
            />
          )}

          {/* Main content area */}
          <div className="flex-1 flex">
            {/* Sidebar with unscheduled posts */}
            <CalendarSidebar
              isOpen={sidebarOpen}
              onToggle={toggleSidebar}
              posts={sidebarPosts}
              totalCount={totalPostsCount}
              hasMore={hasNextPage}
              isLoadingMore={isFetchingNextPage}
              isLoading={isLoadingPosts}
              onLoadMore={() => fetchNextPage()}
              selectedPostId={undefined}
              selectedVariationId={undefined}
              onPostSelect={handleSidebarPostSelect}
              onVariationSelect={handleSidebarVariationSelect}
              onOpenFilters={() => setIsFilterDrawerOpen(true)}
              onEditPost={handleSidebarEditPost}
              onDeletePost={handleSidebarDeletePost}
              onEditVariation={handleSidebarEditVariation}
              onDeleteVariation={handleSidebarDeleteVariation}
              canDeletePost={permissions.canDeletePost}
              onDownloadCalendar={handleDownloadCalendar}
              onResetCalendar={handleResetCalendar}
              onManageHolidays={handleManageHolidays}
              onImportICS={handleImportICS}
              onManageMappingRules={handleManageMappingRules}
            />

            {/* Calendar view */}
            <main className="flex-1 flex flex-col bg-gray-50">
              {/* Calendar header - sticky at top */}
              <CalendarHeader
                hasCalendars={calendars && calendars.length > 0}
                onCreateCalendar={canCreateCalendar ? () => setShowCalendarModal(true) : undefined}
                onSchedulePost={() => handleCreatePost()}
                onDownloadCalendar={handleDownloadCalendar}
                onResetCalendar={handleResetCalendar}
                onManageHolidays={handleManageHolidays}
                onImportICS={handleImportICS}
                onManageMappingRules={handleManageMappingRules}
              >
                <CalendarSelector
                  selectedIds={selectedCalendarIds}
                  onSelectionChange={setSelectedCalendarIds}
                  multiple
                  placeholder={t('select_calendars')}
                  pinnedId={pinnedCalendarId}
                  onPinChange={handlePinChange}
                />
              </CalendarHeader>

              {SHOW_PLANNING_RUNWAY && (
                <PlanningRunway onPlanDate={handlePlanRunwayDay} canPlan={permissions.isCreator} />
              )}

              {/* Calendar content - CalendarGrid handles its own scrolling */}
              <div className="flex-1">
                {/* Loading state while fetching calendars */}
                {(isLoadingCalendars || isLoadingDefaultCalendar) ? (
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
                      <p className="text-gray-500">{t('loading_calendars')}</p>
                    </div>
                  </div>
                ) : isReviewer && (!calendars || calendars.length === 0) ? (
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center max-w-md px-4">
                      <div className="mb-4">
                        <Icon name={ICONS.CALENDAR} size="lg" color="text-gray-400" ariaHidden />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {t('no_calendars_available')}
                      </h3>
                      <p className="text-gray-600 mb-4">
                        {getRestrictionMessage('create calendar') || t('no_calendars_client_message')}
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    {view === 'month' && (
                      <div className="h-full">
                        <CalendarGrid
                          view={view}
                          onViewChange={setView}
                          platform={platformFilter !== 'ALL' ? platformFilter : undefined}
                          calendarIds={selectedCalendarIds}
                          onDateSelect={handleDateSelect}
                          selectedDate={selectedDate}
                          onPostRescheduled={handlePostRescheduled}
                          isDraggingActive={!!activePost || !!activeVariation}
                          onCampaignClick={(campaign) => openCampaignModal(undefined, campaign)}
                          onAddPost={handleCreatePost}
                          onAddCampaign={(date) => openCampaignModal(date, undefined, false, t('create_campaign', { defaultValue: 'Create Campaign' }))}
                          onAddContentPillar={(date) => openCampaignModal(date, undefined, true, t('create_content_pillar', { defaultValue: 'Create Content Pillar' }))}
                          onPostClick={handlePostClick}
                          onPostDelete={requestRemoveFromCalendar}
                          calendarsById={calendarsById}
                        />
                      </div>
                    )}

                    {view === 'week' && (
                      <CalendarWeekView
                        view={view}
                        onViewChange={setView}
                        currentDate={selectedDate || new Date()}
                        onDateChange={(date) => setSelectedDate(date)}
                        onDateSelect={handleDateSelect}
                        calendarIds={selectedCalendarIds}
                        platform={platformFilter !== 'ALL' ? platformFilter : undefined}
                        isDraggingActive={!!activePost || !!activeVariation}
                        onCampaignClick={(campaign) => openCampaignModal(undefined, campaign)}
                        onAddPost={handleCreatePost}
                        onAddCampaign={(date) => openCampaignModal(date, undefined, false, t('create_campaign', { defaultValue: 'Create Campaign' }))}
                        onAddContentPillar={(date) => openCampaignModal(date, undefined, true, t('create_content_pillar', { defaultValue: 'Create Content Pillar' }))}
                        onPostClick={handlePostClick}
                        onPostDelete={requestRemoveFromCalendar}
                        calendarsById={calendarsById}
                      />
                    )}

                    {view === 'day' && (
                      <CalendarDayView
                        view={view}
                        onViewChange={setView}
                        currentDate={selectedDate || new Date()}
                        onDateChange={(date: Date) => setSelectedDate(date)}
                        onDateSelect={handleDateSelect}
                        onEditPost={handleEditPost}
                        onPostDelete={requestRemoveFromCalendar}
                        calendarIds={selectedCalendarIds}
                        platform={platformFilter !== 'ALL' ? platformFilter : undefined}
                        isDraggingActive={!!activePost || !!activeVariation}
                        onCampaignClick={(campaign) => openCampaignModal(undefined, campaign)}
                        onAddPost={handleCreatePost}
                        onAddCampaign={(date) => openCampaignModal(date, undefined, false, t('create_campaign', { defaultValue: 'Create Campaign' }))}
                        onAddContentPillar={(date) => openCampaignModal(date, undefined, true, t('create_content_pillar', { defaultValue: 'Create Content Pillar' }))}
                      />
                    )}
                  </>
                )}
              </div>
            </main>
          </div>

          {/* Day panel (slide-in from right) */}
          {selectedDate && (
            <CalendarDayPosts
              date={selectedDate}
              posts={selectedPosts}
              isOpen={isDayPanelOpen}
              onClose={handleCloseDayPanel}
              onCreatePost={handleCreatePost}
              onEditPost={handleEditPost}
              onDeletePost={requestRemoveFromCalendar}
              onReschedulePost={handleReschedulePost}
              onMapPost={(date) => {
                // Open sidebar if closed and highlight the date for drag-drop mapping
                if (!sidebarOpen) {
                  toggleSidebar();
                }
                setSelectedDate(date);
                // Close the day panel so user can drag from sidebar
                setIsDayPanelOpen(false);
              }}
              onCreateCampaign={(date) => {
                // Open campaign modal with pre-filled date
                openCampaignModal(date);
                setIsDayPanelOpen(false);
              }}
              onCampaignClick={(campaign) => openCampaignModal(undefined, campaign)}
              onDeleteCampaign={handleDeleteCampaign}
              calendars={calendars}
              campaigns={selectedDateCampaigns}
              holidays={selectedDateHolidays}
              onManageHolidays={handleManageHolidays}
            />
          )}

          {/* Drag overlay */}
          <DragOverlay>
            {activePost && (
              <div className="w-64 p-4 bg-white rounded-lg shadow-2xl border-2 border-primary-500">
                <p className="text-sm line-clamp-2 font-medium whitespace-pre-wrap">{activePost.caption}</p>
                {/* Platform icons */}
                {activePost.postChannels && activePost.postChannels.length > 0 && (
                  <div className="flex items-center gap-2 mt-2">
                    {[...new Set(activePost.postChannels.map(pc => pc.channel.platform))].map((platform) => (
                      <FontAwesomeIcon
                        key={platform}
                        icon={platformIcons[platform] || faCloud}
                        className={`w-4 h-4 ${platformColors[platform] || 'text-gray-400'}`}
                        aria-hidden="true"
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
            {activeVariation && (
              <div className="w-64 p-4 bg-white rounded-lg shadow-2xl border-2 border-secondary-500">
                {/* Variation badge */}
                <div className="flex items-center gap-1.5 mb-2">
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-secondary-100 text-secondary-700">
                    <Icon name={ICONS.COPY} size="xs" ariaHidden />
                    {activeVariation.title}
                  </span>
                </div>
                {/* Caption */}
                <p className="text-sm line-clamp-2 font-medium">
                  {activeVariation.caption || activeVariation.post?.caption || 'No caption'}
                </p>
                {/* Platform icons from parent post */}
                {activeVariation.post?.postChannels && activeVariation.post.postChannels.length > 0 && (
                  <div className="flex items-center gap-2 mt-2">
                    {[...new Set(activeVariation.post.postChannels.map(pc => pc.channel.platform))].map((platform) => (
                      <FontAwesomeIcon
                        key={platform}
                        icon={platformIcons[platform] || faCloud}
                        className={`w-4 h-4 ${platformColors[platform] || 'text-gray-400'}`}
                        aria-hidden="true"
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </DragOverlay>
        </div>

        {/* Modals */}
        <CalendarManagementModal
          isOpen={showCalendarModal}
          onClose={() => setShowCalendarModal(false)}
        />

        {/* Post Creator Modal - handles create, edit, and shooting modes */}
        <PostCreator
          isOpen={isPostCreatorOpen}
          onClose={handleClosePostCreator}
          initialScheduledDate={editingCalendarMappingId ? undefined : (selectedDate || undefined)}
          initialCalendarId={selectedDate ? selectedCalendarIds?.[0] : undefined}
          editingPostId={editingPostId || undefined}
          shootingMappingId={editingCalendarMappingId || undefined}
          autoOpenScheduleOnShoot={false}
          onSaveSuccess={async () => {
            // Refetch calendarPosts (awaited) to ensure cache is fully updated
            // before refreshDayDrawer runs. Other queries can invalidate in background.
            invalidateRelease();
            await queryClient.refetchQueries({ queryKey: teamQueryKeys.calendarPosts.all(teamId) });

            // Explicitly refetch sidebar posts to ensure immediate update
            refetchSidebarPosts();

            // Read day posts from cache (just refreshed above) instead of separate API call
            if (isDayPanelOpen && selectedDate) {
              const cachedPosts = getDayPostsFromCache(selectedDate);
              if (cachedPosts !== null) {
                setSelectedPosts(cachedPosts);
              } else {
                await refreshDayDrawer();
              }
            }

            // Now close modal - drawer already has fresh data
            handleClosePostCreator();
          }}
        />

        {/* VariationCreator Modal - for editing variations from sidebar or shooting mapped variations */}
        <VariationCreator
          isOpen={isVariationCreatorOpen}
          onClose={handleCloseVariationCreator}
          variation={editingVariation || undefined}
          shootingMappingId={shootingVariationMappingId || undefined}
          autoOpenScheduleOnShoot={false}
          onSuccess={handleVariationEditSuccess}
        />

        {/* Time Picker Modal */}
        {pendingMapping && (
          <TimePickerModal
            key={`${pendingMapping.post.id}:${pendingMapping.variationId ?? 0}:${pendingMapping.date.getTime()}:${pendingMapping.source}`}
            isOpen={mappingModalOpen}
            onClose={handleMappingModalClose}
            post={pendingMapping.post}
            selectedDate={pendingMapping.date}
            onConfirm={handleMappingConfirm}
            isLoading={createMappingMutation.isPending}
            calendars={pendingMapping.source === 'sidebar' ? mappingCalendars : undefined}
            variationTitle={pendingMapping.variation?.title}
            fromShelf={pendingMapping.fromShelf}
          />
        )}

        {/* Campaign Modal */}
        <CampaignModal
          isOpen={campaignModalOpen}
          onClose={() => setCampaignModalOpen(false)}
          calendarId={selectedCalendarIds[0] || 0}
          initialDate={campaignDate || undefined}
          campaign={editingCampaign || undefined}
          onSubmit={handleCampaignSubmit}
          isLoading={createCampaignMutation.isPending || updateCampaignMutation.isPending}
          showRepetition={campaignShowRepetition}
          modalTitle={campaignModalTitle}
          onDelete={handleDeleteCampaign}
        />

        {/* Reset Calendar Dialog */}
        {selectedCalendarIds.length > 0 && calendars && (
          <ResetCalendarDialog
            isOpen={resetDialogOpen}
            onClose={() => setResetDialogOpen(false)}
            calendarName={
              calendars.find((cal) => cal.id === selectedCalendarIds[0])?.name || 'Calendar'
            }
            onConfirm={handleResetConfirm}
            isLoading={isResetting}
          />
        )}

        {/* Download Calendar Dialog */}
        {selectedCalendarIds.length > 0 && calendars && (
          <DownloadCalendarDialog
            isOpen={downloadDialogOpen}
            onClose={() => setDownloadDialogOpen(false)}
            calendarId={selectedCalendarIds[0]}
            calendarName={
              calendars.find((cal) => cal.id === selectedCalendarIds[0])?.name || 'Calendar'
            }
          />
        )}

        {/* Holidays Modal */}
        {holidaysModalOpen && selectedCalendarIds.length > 0 && calendars && (
          <HolidaysModal
            isOpen={holidaysModalOpen}
            onClose={() => setHolidaysModalOpen(false)}
            calendarId={selectedCalendarIds[0]}
            calendarName={calendars.find((c) => c.id === selectedCalendarIds[0])?.name || 'Calendar'}
          />
        )}

        {/* Import ICS Dialog */}
        {selectedCalendarIds.length > 0 && calendars && (
          <ImportICSDialog
            isOpen={importICSDialogOpen}
            onClose={() => setImportICSDialogOpen(false)}
            calendarId={selectedCalendarIds[0]}
            calendarName={
              calendars.find((cal) => cal.id === selectedCalendarIds[0])?.name || 'Calendar'
            }
            onSuccess={() => {
              invalidateRelease();
              invalidateCalendarOverlay(queryClient);
            }}
          />
        )}

        {/* Mapping Rules Modal */}
        {selectedCalendarIds.length > 0 && calendars && (
          <MappingRulesModal
            isOpen={mappingRulesModalOpen}
            onClose={() => setMappingRulesModalOpen(false)}
            calendarId={selectedCalendarIds[0]}
            calendarName={
              calendars.find((cal) => cal.id === selectedCalendarIds[0])?.name || 'Calendar'
            }
          />
        )}

        {/* Post Filter Drawer */}
        <PostFilterDrawer
          isOpen={isFilterDrawerOpen}
          onClose={() => setIsFilterDrawerOpen(false)}
        />

        {/* One confirmation for Remove from calendar in every calendar view. */}
        <ConfirmDialog
          open={removeFromCalendarTarget !== null}
          onOpenChange={(open) => !open && setRemoveFromCalendarTarget(null)}
          title={t('remove_from_calendar')}
          description={
            <>
              <p>{t('remove_from_calendar_confirm')}</p>
              {removeFromCalendarTarget && getInferredStatus(removeFromCalendarTarget) === 'SCHEDULED' && (
                <p className="mt-3">{t('remove_from_calendar_scheduled_warning')}</p>
              )}
            </>
          }
          variant="warning"
          confirmText={t('remove')}
          cancelText={t('cancel')}
          onConfirm={confirmRemoveFromCalendar}
          closeOnConfirm={false}
        />

        {/* Delete Post Confirmation */}
        <ConfirmationModal
          open={deletePostTarget !== null}
          onOpenChange={(open) => !open && setDeletePostTarget(null)}
          title={t('confirm_delete_title', { defaultValue: 'Delete Post' })}
          description={t('confirm_delete_post', { defaultValue: 'Are you sure you want to delete this post? This action cannot be undone.' })}
          confirmText={t('delete', { defaultValue: 'Delete' })}
          variant="danger"
          onConfirm={confirmDeletePost}
        />

        {/* Delete Variation Confirmation */}
        <ConfirmationModal
          open={deleteVariationTarget !== null}
          onOpenChange={(open) => !open && setDeleteVariationTarget(null)}
          title={t('confirm_delete_variation_title', { defaultValue: 'Delete Variation' })}
          description={t('confirm_delete_variation', { defaultValue: 'Are you sure you want to delete this variation? This action cannot be undone.' })}
          confirmText={t('delete', { defaultValue: 'Delete' })}
          variant="danger"
          onConfirm={confirmDeleteVariation}
        />
      </DndContext>
  );
};

export default Calendar;
